import {
  findSuggestions,
  type Suggestion,
} from "@tiptap-pro/extension-tracked-changes";
import type { Editor } from "@tiptap/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { dedupeTiptapSuggestions } from "./tiptapSuggestionAdapter";
import type { TiptapSuggestionsController } from "./types";

const TRACKED_CHANGES_EVENT_NAMES = [
  "trackedChanges:suggestionCreated",
  "trackedChanges:suggestionAccepted",
  "trackedChanges:suggestionRejected",
  "trackedChanges:suggestionRemoved",
  "trackedChanges:suggestionsUpdated",
  "trackedChanges:suggestionChanged",
  "trackedChanges:suggestionRangeChanged",
] as const;

type UseTiptapSuggestionsOptions = {
  disabled?: boolean;
  editor: Editor | null;
};

const getReadyEditor = (editor: Editor | null): Editor | null => {
  if (!editor || editor.isDestroyed) {
    return null;
  }

  try {
    void editor.commands;
    editor.can();
  } catch {
    return null;
  }

  return editor;
};

const getCurrentSuggestions = (editor: Editor | null): Suggestion[] => {
  const readyEditor = getReadyEditor(editor);

  if (!readyEditor) {
    return [];
  }

  try {
    return dedupeTiptapSuggestions(findSuggestions(readyEditor));
  } catch {
    return [];
  }
};

export const useTiptapSuggestions = ({
  disabled = false,
  editor,
}: UseTiptapSuggestionsOptions): TiptapSuggestionsController => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>(() =>
    getCurrentSuggestions(editor)
  );

  const refreshSuggestions = useCallback(() => {
    setSuggestions(getCurrentSuggestions(editor));
  }, [editor]);

  useEffect(() => {
    refreshSuggestions();

    if (!editor) {
      return;
    }

    TRACKED_CHANGES_EVENT_NAMES.forEach((eventName) => {
      editor.on(eventName, refreshSuggestions);
    });
    editor.on("update", refreshSuggestions);

    return () => {
      TRACKED_CHANGES_EVENT_NAMES.forEach((eventName) => {
        editor.off(eventName, refreshSuggestions);
      });
      editor.off("update", refreshSuggestions);
    };
  }, [editor, refreshSuggestions]);

  const acceptSuggestion = useCallback(
    (id: string) => {
      const readyEditor = getReadyEditor(editor);

      if (!readyEditor || disabled) {
        return false;
      }

      const accepted = readyEditor.commands.acceptSuggestion({ id });
      refreshSuggestions();
      return Boolean(accepted);
    },
    [disabled, editor, refreshSuggestions]
  );

  const rejectSuggestion = useCallback(
    (id: string) => {
      const readyEditor = getReadyEditor(editor);

      if (!readyEditor || disabled) {
        return false;
      }

      const rejected = readyEditor.commands.rejectSuggestion({ id });
      refreshSuggestions();
      return Boolean(rejected);
    },
    [disabled, editor, refreshSuggestions]
  );

  const acceptAllSuggestions = useCallback(() => {
    const readyEditor = getReadyEditor(editor);

    if (!readyEditor || disabled) {
      return false;
    }

    const accepted = readyEditor.commands.acceptAllSuggestions();
    refreshSuggestions();
    return Boolean(accepted);
  }, [disabled, editor, refreshSuggestions]);

  const rejectAllSuggestions = useCallback(() => {
    const readyEditor = getReadyEditor(editor);

    if (!readyEditor || disabled) {
      return false;
    }

    const rejected = readyEditor.commands.rejectAllSuggestions();
    refreshSuggestions();
    return Boolean(rejected);
  }, [disabled, editor, refreshSuggestions]);

  return useMemo(
    () => ({
      acceptAllSuggestions,
      acceptSuggestion,
      disabled,
      editor,
      refreshSuggestions,
      rejectAllSuggestions,
      rejectSuggestion,
      suggestions,
    }),
    [
      acceptAllSuggestions,
      acceptSuggestion,
      disabled,
      editor,
      refreshSuggestions,
      rejectAllSuggestions,
      rejectSuggestion,
      suggestions,
    ]
  );
};
